import { Formik } from 'formik'
import { keys, isEmpty } from 'lodash'
import { useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { Tag } from 'components/v2/Tag'
import { getQueryString } from 'utils/route'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { Select } from 'components/v2/Select'
import { DateRangeFilter } from 'features/App/components/Filters/DateRangeFilter'
import { InventoryAutocomplete, getInventoryLabel } from 'features/Inventories/components/InventoryAutocomplete'
import { SALE_STATUS } from 'features/Sales/components/Status/constants'
import { saleStatusMessages } from 'features/Sales/components/Status/messages'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'
import { UserAutocomplete } from 'features/Users/components/UserAutocomplete'
import { dateRangeToString, getDateRangeQuery } from 'utils/date'
import { sortByTranslation, safeFormatMessage } from 'utils/intl'

const SALE_STATUS_LIST = [SALE_STATUS.CONFIRMED, SALE_STATUS.CANCELLED]

const messages = defineMessages({
  idPlaceholder: {
    id: 'spanish.sales.filters.idPlaceholder',
    defaultMessage: 'Id'
  },
  selectStatus: {
    id: 'spanish.sales.filters.selectStatus',
    defaultMessage: 'Estado'
  },
  selectPurchaseDateRange: {
    id: 'spanish.sales.filters.selectPurchaseDateRange',
    defaultMessage: 'Fecha de compra'
  },
  sellerIdPlaceholder: {
    id: 'spanish.sales.filters.selectSeller',
    defaultMessage: 'Vendedor'
  },
  cartIdPlaceholder: {
    id: 'spanish.sales.filters.cartIdPlaceholder',
    defaultMessage: 'Lista de carritos'
  }
})

export const SalesFilters = ({ filters, companyId }: TSFixMe): TSFixMe => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): TSFixMe => {
    showFilters(!isShowingFilters)
  }

  const sortSalesStatusByTranslation = (): TSFixMe => {
    return SALE_STATUS_LIST.sort((a, b): TSFixMe => sortByTranslation(a, b, intl, saleStatusMessages))
  }

  const getCashierLabel = (cashier: TSFixMe): TSFixMe => {
    if (cashier.username) {
      return `${cashier.firstName} ${cashier.lastName} (${cashier.username})`
    }

    if (cashier.firstName) {
      return cashier.firstName
    }

    return ''
  }

  const filterSales = (values: TSFixMe): TSFixMe => {
    const purchaseDateRangeQuery = getDateRangeQuery(values.purchaseDateRange)

    const query = {
      id: values.id,
      status: values.status,
      purchaseDateFrom: purchaseDateRangeQuery.from,
      purchaseDateTo: purchaseDateRangeQuery.to,
      purchaseDateRangeValue: purchaseDateRangeQuery.rangeValue,
      sellerId: values.seller && values.seller.id,
      cartId: values.cart && values.cart.id
    }

    navigate(`/sales?${getQueryString(query)}`)
  }

  const initialValues = {
    id: filters.id || '',
    status: filters.status || '',
    purchaseDateRange: filters.purchaseDateRange || {
      from: '',
      to: '',
      rangeValue: ''
    },
    seller: filters.seller || { id: '' },
    cart: filters.cart || { id: '' }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterSales} enableReinitialize>
      {(props): TSFixMe => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleSelectChange = (event: TSFixMe): TSFixMe => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          filterSales({
            ...values,
            [id]: value
          })
        }

        const handleFilterDelete = (id: TSFixMe): TSFixMe => {
          setFieldValue(id, '')
          filterSales({
            ...values,
            [id]: ''
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterToggle onClick={toggleFilters} />
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey]
                      const hasValue = (filterValue && filterValue.getTime) || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'id':
                          label = <FormattedMessage id="spanish.dictionary.id" defaultMessage="Id" />
                          value = filterValue
                          break
                        case 'status':
                          label = <FormattedMessage id="spanish.dictionary.status" defaultMessage="Estado" />
                          value = safeFormatMessage({ intl, messages: saleStatusMessages, key: filterValue })
                          break
                        case 'purchaseDateRange':
                          label = (
                            <FormattedMessage id="spanish.dictionary.purchaseDate" defaultMessage="Fecha de compra" />
                          )
                          value = dateRangeToString(intl, filterValue)

                          if (!value) return null

                          break
                        case 'seller':
                          label = <FormattedMessage id="spanish.dictionary.sellerId" defaultMessage="Vendedor" />
                          value = getCashierLabel(filterValue)
                          break
                        case 'cart':
                          label = <FormattedMessage id="spanish.dictionary.cartId" defaultMessage="Lista de carritos" />
                          value = getInventoryLabel(filterValue, intl)
                          break
                      }

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey)}>
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="id"
                    value={values.id}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.idPlaceholder)}
                    label={<FormattedMessage id="spanish.dictionary.id" defaultMessage="Id" />}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="spanish.dictionary.status" defaultMessage="Estado" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
                    {sortSalesStatusByTranslation(values.status).map((status: TSFixMe): TSFixMe => {
                      return (
                        <option value={status} key={status}>
                          {intl.formatMessage(saleStatusMessages[status])}
                        </option>
                      )
                    })}
                  </Select>
                  <div>
                    <Label>
                      <FormattedMessage id="spanish.dictionary.purchaseDateRange" defaultMessage="Fecha de compra" />
                    </Label>
                    <DateRangeFilter
                      value={values.purchaseDateRange}
                      placeholder={intl.formatMessage(messages.selectPurchaseDateRange)}
                      onChange={(value): TSFixMe => {
                        setFieldValue('purchaseDateRange', value)

                        filterSales({
                          ...values,
                          purchaseDateRange: value
                        })
                      }}
                    />
                  </div>
                  <InventoryAutocomplete
                    placeholder={intl.formatMessage(messages.cartIdPlaceholder)}
                    value={getInventoryLabel(values.cart, intl)}
                    label={<FormattedMessage id="spanish.dictionary.cartId" defaultMessage="Carrito de compras" />}
                    onChange={(cart: TSFixMe): TSFixMe => {
                      setFieldValue('cart', cart)

                      filterSales({
                        ...values,
                        cart
                      })
                    }}
                    companyId={companyId}
                  />
                  <UserAutocomplete
                    placeholder={intl.formatMessage(messages.sellerIdPlaceholder)}
                    value={getCashierLabel(values.seller)}
                    label={<FormattedMessage id="dictionary.user" defaultMessage="Vendedor" />}
                    onChange={(seller: TSFixMe): TSFixMe => {
                      setFieldValue('sellerId', seller)

                      filterSales({
                        ...values,
                        seller
                      })
                    }}
                    getItemLabel={getCashierLabel}
                    isAssigned
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
